import { DateRangeTypes } from '@business/entities/dynamicReprots';
import { R } from '@core/utils/r';
import { defineReportViewForm } from '@modules/dynamicReports/ReportBuilderOverlay/Components/ReportFilters';
import { ReportViewer } from '@modules/dynamicReports/ReportBuilderOverlay/Components/ReportViewer';
import 'chartist/dist/scss/chartist.scss';
import { MDBDataTable } from 'mdbreact';
import React, { memo, useMemo } from 'react';
import ScrollBar from 'react-perfect-scrollbar';
import { Card, CardTitle } from 'reactstrap';
import { WidgetProps } from '../types';
import './index.scss';
import { WidgetIcons } from '../BaseWidget';
import { DashboardInterval } from '@business/entities/dashboard';
import { toMoment } from '@core/utils/time';
import { useCompany } from '@core/utils/hooks/misc';

export const ListWidget = memo((props: WidgetProps<string>) => {
  const { settings, result, data, dateRange, print = false } = props;
  const company = useCompany();

  const calculatedDateRange = useMemo(() => {
    const startDate = dateRange ? toMoment(R.toNumber(dateRange.split('-')[0])) : toMoment();
    if (print) return dateRange;
    if (settings.interval === DashboardInterval.yearly) {
      return `${startDate.startOf('year').unix()}-${startDate.endOf('year').unix()}`;
    } else if (settings.interval === DashboardInterval.monthly) {
      return `${startDate.startOf('month').unix()}-${startDate.endOf('month').unix()}`;
    } else if (settings.interval === DashboardInterval.daily) {
      return `${startDate.startOf('day').unix()}-${startDate.endOf('day').unix()}`;
    } else if (settings.interval === DashboardInterval.weekly) {
      const myIsoWeekDay = company.weekendingDay;
      let startTime = toMoment();
      let endTime = startTime.clone();

      if (toMoment().day() == myIsoWeekDay) {
        startTime = toMoment().add(-6, 'days').startOf('day');
        endTime = toMoment().endOf('day');
      } else {
        const daysToSubtract =
          toMoment().isoWeekday() >= myIsoWeekDay ? toMoment().isoWeekday() - myIsoWeekDay : 7 + toMoment().isoWeekday() - myIsoWeekDay;

        startTime = toMoment().add(-daysToSubtract, 'd').add(1, 'd').startOf('day');
        endTime = startTime.clone().add(6, 'days').endOf('day');
      }
      return `${startTime.unix()}-${endTime.unix()}`;
    }
    return dateRange;
  }, [company.weekendingDay, dateRange, print, settings.interval]);

  const filterForm = useMemo(
    () =>
      defineReportViewForm(
        ((R.get(data, 'reportFilters') as string[]) || []).map(key => data.source.mapping[key]),
        R.get(data, 'defaultDateRange') || DateRangeTypes.month,
        calculatedDateRange,
      ),
    [calculatedDateRange, data],
  );

  const tableData = useMemo(
    () => ({
      columns: [
        {
          label: 'Order ID',
          field: 'orderId',
          sort: 'asc',
        },
        {
          label: 'Billing Name',
          field: 'billingName',
          sort: 'asc',
        },
        {
          label: 'Payment Method',
          field: 'paymentMethod',
          sort: 'asc',
        },
        {
          label: 'View Details',
          field: 'action',
          sort: 'asc',
        },
      ],
      rows: [],
    }),
    [],
  );
  return (
    <Card className="p-4" style={{ marginBottom: 0, flex: 1 }}>
      <CardTitle className="d-flex align-items-center flex-row">
        {settings.title || 'List'} <WidgetIcons {...props} />
      </CardTitle>
      <div className="d-flex flex-column list-widget" style={{ overflow: 'hidden' }}>
        {data ? (
          <ScrollBar style={{ width: '100%' }}>
            <ReportViewer report={data} form={filterForm} builder print={props.print} widget hideDownload={props.print} />
          </ScrollBar>
        ) : (
          <MDBDataTable
            responsive
            striped
            bordered
            data={tableData}
            paging={false}
            searching={false}
            noBottomColumns
            displayEntries={false}
          ></MDBDataTable>
        )}
      </div>
    </Card>
  );
});
